import React from "react";
import { Link } from "react-router-dom";

const LAST_UPDATED = "February 23, 2026";

export default function Methodology() {
  return (
    <div data-testid="methodology-page" className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
      <div className="label mb-4 text-[hsl(var(--accent))]">Methodology</div>
      <h1 className="serif text-5xl text-foreground leading-tight">
        How Correlate reads the traditions.
      </h1>
      <p className="mt-4 mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        Last updated · {LAST_UPDATED}
      </p>

      <Section title="The short version">
        <p>
          Correlate is an <em>interpretive archive</em>, not a citation database.
          Our tradition primers are hand-composed summaries that draw on canonical
          teachings and widely-taught surveys. Our Oracle is an AI synthesis engine.
          Both are meant to spark curiosity across traditions — not to replace
          primary sources, teachers, or peer-reviewed scholarship.
        </p>
        <p className="mt-4">
          If you plan to quote, teach, or publish from anything you read here,
          verify against the primary text of the tradition first.
        </p>
      </Section>

      <Section title="How the tradition primers are written">
        <p>
          Every entry on our <Link to="/traditions" className="link-underline text-[hsl(var(--primary))]">Traditions</Link> index
          (Christianity, Vedanta, Sufism, Stoicism, Determinism, Quantum Physics,
          and the rest) is a short primer — era, region, a summary, four to five
          core beliefs, a handful of practices, key figures, and a short list of
          correlations with other traditions.
        </p>
        <p className="mt-4">
          These primers are composed by hand, in a consistent voice, drawing on
          the kind of introductory material a first-year religion, philosophy,
          or history-of-science student would encounter. Our reference points
          include:
        </p>
        <ul className="mt-4 space-y-2 list-disc pl-6 marker:text-[hsl(var(--accent))]">
          <li>
            <strong>Reference works</strong> — the{" "}
            <a href="https://plato.stanford.edu" target="_blank" rel="noopener noreferrer" className="link-underline text-[hsl(var(--primary))]">
              Stanford Encyclopedia of Philosophy
            </a>
            , <a href="https://www.britannica.com" target="_blank" rel="noopener noreferrer" className="link-underline text-[hsl(var(--primary))]">
              Encyclopædia Britannica
            </a>, the Oxford and Cambridge Companion series, and the{" "}
            <a href="https://iep.utm.edu" target="_blank" rel="noopener noreferrer" className="link-underline text-[hsl(var(--primary))]">
              Internet Encyclopedia of Philosophy
            </a>.
          </li>
          <li>
            <strong>Primary texts</strong> — the Bible, the Qur&apos;an, the Upanishads,
            the Pali Canon, the Tao Te Ching, the Zohar, the Corpus Hermeticum,
            Plato&apos;s dialogues, Marcus Aurelius, the Ra material, and the like.
          </li>
          <li>
            <strong>Institutional and community sources</strong> — official teaching
            bodies where they exist (e.g. the Vatican, Chabad, the Office of the
            Dalai Lama), and long-standing community references such as{" "}
            <a href="https://sacred-texts.com" target="_blank" rel="noopener noreferrer" className="link-underline text-[hsl(var(--primary))]">
              sacred-texts.com
            </a>.
          </li>
          <li>
            <strong>Peer-reviewed surveys</strong> — the standard university
            introductions to religious studies, comparative philosophy, and the
            history and philosophy of science.
          </li>
        </ul>
        <p className="mt-4">
          Individual sentences in a primer are not footnoted. If you spot a claim
          you believe is wrong or reductive, please{" "}
          <a href="mailto:hello@correlate.online" className="link-underline text-[hsl(var(--primary))]">
            write to us
          </a>{" "}
          — we take corrections seriously and revise entries when they&apos;re warranted.
        </p>
      </Section>

      <Section title="How the Oracle works">
        <p>
          The Oracle is a large language model — <strong>Anthropic&apos;s Claude
          Sonnet 4.5</strong> — instructed to answer as a scholar-mystic who maps
          a term across three to five traditions. Every Oracle response is
          <em> generated live</em> from your question. It is not retrieved from a
          fixed corpus, and it does not attach source URLs.
        </p>
        <p className="mt-4">
          Because Claude was trained on a broad body of public text, its answers
          reflect the general shape of how these traditions are described in
          academic and popular literature. It can be wrong. It can hallucinate a
          reference. It can smooth over important disagreements. Treat every
          Oracle response as a starting point for your own reading, never as
          the final word.
        </p>
        <p className="mt-4">
          We surface a small disclosure on the Oracle page itself. If you plan to
          share an Oracle answer publicly, we recommend also naming the source
          traditions it cites and checking their primary texts.
        </p>
      </Section>

      <Section title="What correlations mean here">
        <p>
          A <em>correlation</em> on Correlate is a claim of{" "}
          <em>family resemblance</em> — that two traditions, in different
          vocabularies, are gesturing at something structurally similar. It is
          not a claim that the two traditions are identical, that one is derived
          from the other, or that all thoughtful practitioners of both would
          agree with the comparison.
        </p>
        <p className="mt-4">
          Correlations are proposed by the archive, by the Oracle, and by our
          community. They are meant to open a conversation, not close one.
          Disagreement — from within any tradition — is expected and welcome.
        </p>
      </Section>

      <Section title="Community-contributed content">
        <p>
          Weekly posts, comments, and reactions come from real people in the
          Correlate community. They pass through basic moderation but are not
          fact-checked. Opinions expressed there belong to their authors and do
          not represent the archive.
        </p>
      </Section>

      <Section title="Corrections and revisions">
        <p>
          Because our primers are living summaries, they will change over time
          as we get better feedback from teachers, practitioners, and readers.
          If you&apos;re a scholar or long-time practitioner of a tradition we
          cover and you see something worth correcting, please write to us at{" "}
          <a href="mailto:corrections@correlate.online" className="link-underline text-[hsl(var(--primary))]">
            corrections@correlate.online
          </a>. We&apos;ll credit you if you&apos;d like.
        </p>
      </Section>

      <Section title="A note on tone">
        <p>
          Correlate&apos;s voice is warm, plain, and intentionally light on jargon.
          That is a choice — we&apos;re building a doorway, not a defended thesis.
          Depth lives in the sources we point to.
        </p>
      </Section>

      <p className="mt-16 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <Link to="/traditions" className="link-underline" data-testid="methodology-to-traditions">
          Return to the Traditions index →
        </Link>
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="serif text-2xl text-foreground mb-3">{title}</h2>
      <div className="serif text-foreground/85 leading-relaxed text-[16px]">
        {children}
      </div>
    </section>
  );
}
